import { AppImageData, AppImagePathEntry } from '@components/appImage';
import { BgPosition } from '@components/card';
import { MappedFaqItem } from '@components/faqSection';
import { HowItWorksStep } from '@components/howItWorks';
import { WhyChoosePlatformCard } from '@components/whyChoosePlatform';
import { createElement } from 'react';

import { MainCardConfig } from './components/mainCardsSection';

import { ArrowCounterclockwiseClockStroke48 } from '@/shared/icons/48/ArrowCounterclockwiseClockStroke48';
import { BottomLeftToPointToTopRightFill48 } from '@/shared/icons/48/BottomLeftToPointToTopRightFill48';
import { LocationEllipseFill48 } from '@/shared/icons/48/LocationEllipseFill48';
import { LocationWavesNorthFill48 } from '@/shared/icons/48/LocationWavesNorthFill48';
import { MapPinStroke48 } from '@/shared/icons/48/MapPinStroke48';
import { PointBottomLeftToTopRightFill48 } from '@/shared/icons/48/PointBottomLeftToTopRightFill48';
import { RouteMiniMapStroke48 } from '@/shared/icons/48/RouteMiniMapStroke48';
import { Domain, Locale } from '@/shared/types';
import { getDocsUrl } from '@/shared/utils/getDocsUrl';

/**
 * Генерирует константы с переводами для страницы Routing API
 * @param t - объект с переводами
 */
export const getTranslatedConstants = (
  t: Record<string, string>,
): {
  WHY_CHOOSE_ROUTING: WhyChoosePlatformCard[];
  FAQ_ITEMS: MappedFaqItem[];
  MAIN_CARDS: MainCardConfig[];
  HOW_IT_WORKS_STEPS: HowItWorksStep[];
} => ({
  WHY_CHOOSE_ROUTING: [
    {
      id: 'routing-api',
      title: t.apiRoutingName,
      description: t.routingApiDescription,
      icon: createElement(BottomLeftToPointToTopRightFill48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref: 'https://docs.2gis.com/api/navigation/routing/overview',
    },
    {
      id: 'distance-matrix-api',
      title: t.apiDistanceMatrixName,
      description: t.distanceMatrixApiDescription,
      icon: createElement(ArrowCounterclockwiseClockStroke48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref:
        'https://docs.2gis.com/api/navigation/distance-matrix/overview',
    },
    {
      id: 'tsp-api',
      title: t.apiTspName,
      description: t.tspApiDescription,
      icon: createElement(LocationWavesNorthFill48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref: 'https://docs.2gis.com/api/navigation/tsp/overview',
    },
    {
      id: 'isochrone-api',
      title: t.apiIsochroneName,
      description: t.isochroneApiDescription,
      icon: createElement(RouteMiniMapStroke48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref: 'https://docs.2gis.com/api/navigation/isochrone/overview',
    },
    {
      id: 'map-matching-api',
      title: t.mapMatchingApiTitle,
      description: t.mapMatchingApiDescription,
      icon: createElement(PointBottomLeftToTopRightFill48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref: 'https://docs.2gis.com/api/navigation/map-matching/overview',
    },
    {
      id: 'radar-api',
      title: t.apiRadarName,
      description: t.radarApiDescription,
      icon: createElement(MapPinStroke48),
      buttonLabel: t.buttonMoreDetails,
      buttonHref: 'https://docs.2gis.com/api/navigation/radar/overview',
    },
    {
      id: 'navi-sdk',
      title: t.naviSdkTitle,
      description: t.naviSdkDescription,
      icon: createElement(LocationEllipseFill48),
    },
    {
      id: 'cta',
      type: 'CTA' as const,
      title: t.ctaTitle,
      titleVariant: 'heading-m' as const,
      mainButton: {
        href: 'https://platform.2gis.ru/ru/dashboard?utm_source=dev2gis&utm_medium=why_choose&utm_campaign=routing_api',
        label: t.getDemoKey,
        variant: 'fourth' as const,
      },
      colSpan: 2,
    },
  ],

  MAIN_CARDS: [
    {
      id: 'car',
      title: t.routesForAllCasesTitle,
      description: t.routesForAllBusinessDescription,
      imageSrc: '/images/key-features/car.png',
    },
    {
      id: 'chartbar',
      title: t.thousandFactorsTitle,
      description: t.thousandFactorsDescription,
      imageSrc: '/images/key-features/chartbar.png',
    },
    {
      id: 'earth',
      title: t.coverageRussiaCisTitle,
      description: t.coverageRussiaCisDescription,
      imageSrc: '/images/key-features/earth.png',
    },
  ],

  HOW_IT_WORKS_STEPS: [
    {
      id: 'step-1',
      stepNumber: '01',
      description: t.howItWorksStep1Description,
      buttonLabel: t.personalAccountInPlatformManager,
      pcPlacement: 'bottom-left' as BgPosition,
      mobilePlacement: 'top-left' as BgPosition,
      buttonHref:
        'https://platform.2gis.ru/ru/dashboard?utm_source=dev2gis&utm_medium=how_it_works&utm_campaign=routing',
    },
    {
      id: 'step-2',
      stepNumber: '02',
      description: t.howItWorksStep2Description,
      pcPlacement: 'top-right' as BgPosition,
      mobilePlacement: 'top-right' as BgPosition,
    },
    {
      id: 'step-3',
      stepNumber: '03',
      description: t.howItWorksStep3Description,
      pcPlacement: 'bottom-right' as BgPosition,
      mobilePlacement: 'bottom-left' as BgPosition,
    },
  ],

  FAQ_ITEMS: [
    { type: 'default', key: 'howToGetAccess' },
    {
      id: 'faq-road-graph-update',
      title: t.faqRoadGraphUpdateTitle,
      content: t.faqRoadGraphUpdateContent,
    },
    {
      id: 'faq-traffic-updates',
      title: t.faqTrafficUpdatesTitle,
      content: t.faqTrafficUpdatesContent,
    },
    {
      id: 'faq-routing-points-limit',
      title: t.faqRoutingPointsLimitTitle,
      content: t.faqRoutingPointsLimitContent,
    },
    {
      id: 'faq-mobile-sdk-versions',
      title: t.faqMobileSdkVersionsTitle,
      content: t.faqMobileSdkVersionsContent,
    },
    {
      id: 'faq-mobile-sdk-install',
      title: t.faqMobileSdkInstallTitle,
      content: t.faqMobileSdkInstallContent,
    },
    {
      id: 'faq-full-sdk-integration',
      title: t.faqFullSdkIntegrationTitle,
      content: t.faqFullSdkIntegrationContent,
    },
    {
      id: 'faq-flutter-support',
      title: t.faqFlutterSupportTitle,
      content: t.faqFlutterSupportContent,
    },
    {
      id: 'faq-custom-route-display',
      title: t.faqCustomRouteDisplayTitle,
      content: t.faqCustomRouteDisplayContent,
    },
  ],
});

/**
 * Медиа секции, зависящее от домена: растровая картинка через AppImage
 * либо gif/video из public storage, которые imgproxy не обрабатывает.
 */
export type RoutingMedia =
  | { type: 'image'; data: AppImageData }
  | { type: 'gif'; src: string }
  | { type: 'video'; src: string; poster?: string };
export type DomainMedia = Record<Domain, RoutingMedia>;
export type RoutingFeatureMedia = Record<string, DomainMedia>;
export type RadarApiMedia = Record<Domain, AppImageData>;
export type MobileSdkMedia = Record<Domain, AppImageData>;

/** Размеры рендера (px) для srcset и пропорций next/image */
export const MOBILE_SDK_SCREEN_SIZE = { width: 303, height: 502 };
export const ROUTING_FEATURE_MEDIA_SIZE = { width: 560, height: 360 };

// Оригиналы: ru 782x652, остальные 377x328 — рендер по высоте 326
export const RADAR_API_MEDIA_CONFIG: Record<Domain, AppImagePathEntry> = {
  ru: { path: '/images/api_nav/radar-api/ru.png', width: 391, height: 326 },
  kz: { path: '/images/api_nav/radar-api/kz.png', width: 375, height: 326 },
  com: { path: '/images/api_nav/radar-api/com.png', width: 375, height: 326 },
  am: { path: '/images/api_nav/radar-api/am.png', width: 375, height: 326 },
  ge: { path: '/images/api_nav/radar-api/ge.png', width: 375, height: 326 },
  tj: { path: '/images/api_nav/radar-api/tj.png', width: 375, height: 326 },
};

export const MOBILE_SDK_MEDIA_CONFIG: Record<Domain, string> = {
  ru: '/images/api_nav/mobile-navigation-screen/ru.png',
  kz: '/images/api_nav/mobile-navigation-screen/kz.png',
  com: '/images/api_nav/mobile-navigation-screen/com.png',
  am: '/images/api_nav/mobile-navigation-screen/am.png',
  ge: '/images/api_nav/mobile-navigation-screen/ge.png',
  tj: '/images/api_nav/mobile-navigation-screen/tj.png',
};

export const ROUTING_FEATURE_MEDIA_CONFIG: Record<
  string,
  Record<Domain, string>
> = {
  'consider-real-road-conditions': {
    ru: '/images/api_nav/feature-media/map-matrix/ru.gif',
    kz: '/images/api_nav/feature-media/map-matrix/kz.gif',
    com: '/images/api_nav/feature-media/map-matrix/com.png',
    am: '/images/api_nav/feature-media/map-matrix/am.png',
    ge: '/images/api_nav/feature-media/map-matrix/ge.png',
    tj: '/images/api_nav/feature-media/map-matrix/tj.png',
  },
  'optimize-routes-for-logistics': {
    ru: '/images/api_nav/feature-media/optimize-routes/ru.png',
    kz: '/images/api_nav/feature-media/optimize-routes/kz.png',
    com: '/images/api_nav/feature-media/optimize-routes/com.png',
    am: '/images/api_nav/feature-media/optimize-routes/am.png',
    ge: '/images/api_nav/feature-media/optimize-routes/ge.png',
    tj: '/images/api_nav/feature-media/optimize-routes/tj.png',
  },
  'track-routes-in-real-time': {
    ru: '/images/api_nav/feature-media/track-routes/ru.png',
    kz: '/images/api_nav/feature-media/track-routes/kz.png',
    com: '/images/api_nav/feature-media/track-routes/com.png',
    am: '/images/api_nav/feature-media/track-routes/am.png',
    ge: '/images/api_nav/feature-media/track-routes/ge.png',
    tj: '/images/api_nav/feature-media/track-routes/tj.png',
  },
  'evaluate-accessibility-zones': {
    ru: '/images/api_nav/feature-media/evaluate-accessibility/ru.png',
    kz: '/images/api_nav/feature-media/evaluate-accessibility/kz.png',
    com: '/images/api_nav/feature-media/evaluate-accessibility/com.png',
    am: '/images/api_nav/feature-media/evaluate-accessibility/am.png',
    ge: '/images/api_nav/feature-media/evaluate-accessibility/ge.png',
    tj: '/images/api_nav/feature-media/evaluate-accessibility/tj.png',
  },
};

/**
 * Генерирует карточки функций маршрутизации с переводами
 * @param t - объект с переводами
 * @param locale - текущая локаль, нужна для языкового префикса ссылок на документацию
 */
export const getRoutingFeatureCards = (
  t: Record<string, string>,
  locale: Locale,
) => ({
  // Широкая карточка (2 колонки) с GIF анимацией справа
  considerRealRoadConditions: {
    id: 'consider-real-road-conditions',
    layout: 'horizontal-image-right' as const,
    title: t.considerRealRoadConditionsTitle,
    description: t.considerRealRoadConditionsDescription,
    buttonText: t.buttonMoreDetails,
    buttonHref: getDocsUrl('/api/navigation/distance-matrix/overview', locale),
    imageUrl: 'dynamic' as const,
    imageAlt: 'consider-real-road-conditions',
    className: 'col-span-2',
    useTooltip: true,
  },

  // Вертикальная карточка с изображением сверху
  optimizeRoutesForLogistics: {
    id: 'optimize-routes-for-logistics',
    layout: 'vertical' as const,
    title: t.optimizeRoutesForLogisticsTitle,
    description: t.optimizeRoutesForLogisticsDescription,
    buttonText: t.buttonMoreDetails,
    buttonHref: getDocsUrl('/api/navigation/tsp/overview', locale),
    imageUrl: 'dynamic' as const,
    imageAlt: 'optimize-routes-for-logistics',
  },

  // Вертикальная карточка с изображением сверху
  trackRoutesInRealTime: {
    id: 'track-routes-in-real-time',
    layout: 'vertical' as const,
    title: t.trackRoutesInRealTimeTitle,
    description: t.trackRoutesInRealTimeDescription,
    buttonText: t.buttonMoreDetails,
    buttonHref: getDocsUrl('/api/navigation/map-matching/overview', locale),
    imageUrl: 'dynamic' as const,
    imageAlt: 'track-routes-in-real-time',
  },

  // Широкая карточка (2 колонки) с изображением справа
  evaluateAccessibilityZones: {
    id: 'evaluate-accessibility-zones',
    layout: 'horizontal-image-right' as const,
    title: t.evaluateAccessibilityZonesTitle,
    description: t.evaluateAccessibilityZonesDescription,
    buttonText: t.buttonMoreDetails,
    buttonHref: getDocsUrl('/api/navigation/isochrone/overview', locale),
    imageUrl: 'dynamic' as const,
    imageAlt: 'evaluate-accessibility-zones-isochrones',
    className: 'col-span-2',
  },
});
